import { useEffect, useMemo, useState } from 'react';
import { api, datum, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { fragen } from '../teile/rueckfrage.tsx';

/* BANKVERBINDUNGEN, ZENTRAL FÜR ALLE BEREICHE (.260)
 * ORIGINALTEXT MARVIN (30.09.2026)
 *   „wo verwalte ich denn den zugewiesenen bankverbindunden? das reicht
 *    einmal zentral für alle bereiche."
 *
 * Eine Liste für Gebühren und Rechnungen. Bestätigt heißt: ein Mensch hat
 * Name und IBAN geprüft, nur dorthin geht Geld.
 *
 * AKTUALISIEREN (.265). Marvin: „ich will den Button um die Bankverbindung zu
 * aktualisieren in jedem Bereich haben". Jede Zeile hat ihn, auch für die IBAN:
 * wer sie hier einträgt, hat sie geprüft, sie gilt danach als bestätigt. */

const ART: Record<string, string> = { behoerde: 'Kasse (Behörde)', partner: 'Partner', sonstig: 'Sonstige' };
type Filter = 'alle' | 'behoerde' | 'partner' | 'vorschlag';

function ibanOk(roh: string): boolean {
  const iban = roh.replace(/[\s.\-]/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban) || (iban.startsWith('DE') && iban.length !== 22)) return false;
  let rest = 0;
  for (const z of iban.slice(4) + iban.slice(0, 4)) {
    const w = /\d/.test(z) ? z : String(z.charCodeAt(0) - 55);
    for (const c of w) rest = (rest * 10 + Number(c)) % 97;
  }
  return rest === 1;
}

export default function Bankverbindungen({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<{ empfaenger: any[]; gemeinden: any[]; partner: any[] } | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [filter, setFilter] = useState<Filter>('alle');
  const [suche, setSuche] = useState('');
  const [bearbeiten, setBearbeiten] = useState<any | null>(null);

  async function laden() {
    try { setD(await api.bankverbindungen()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  async function tu(f: () => Promise<string>) {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try { setHinweis(await f()); await laden(); } catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }

  const alle = d?.empfaenger ?? [];
  const zahl = (f: Filter) => f === 'alle' ? alle.length : f === 'vorschlag' ? alle.filter((e) => !e.bestaetigt_am).length : alle.filter((e) => e.art === f).length;
  const sicht = useMemo(() => {
    const s = suche.trim().toLowerCase().replace(/\s/g, '');
    return alle.filter((e) => filter === 'alle' || (filter === 'vorschlag' ? !e.bestaetigt_am : e.art === filter))
      .filter((e) => !s || [e.name, e.iban, e.gemeinde, e.firma, e.notiz].some((w) => String(w ?? '').toLowerCase().replace(/\s/g, '').includes(s)));
  }, [d, filter, suche]);

  return (
    <>
      <Kopf titel="Bankverbindungen" weg="Kassen und Partner, an die gezahlt wird"
            rechts={<button className="knopf haupt" onClick={() => setBearbeiten({ neu: true, art: 'partner' })}>Neue Bankverbindung</button>} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">✅ {hinweis}</div>}
        <div className="freigabekacheln">
          {([['alle', 'alle', ''], ['partner', 'Partner', 'blau'], ['behoerde', 'Kassen', ''], ['vorschlag', 'noch nicht bestätigt', 'warnung']] as const).map(([f, t, farbe]) => (
            <button key={f} type="button" aria-pressed={filter === f} className={`freigabekachel ${farbe} ${filter === f ? 'an' : ''}`} onClick={() => setFilter(f)}>
              <span className="zahl">{zahl(f)}</span><span>{t}</span>
            </button>
          ))}
        </div>
        <Karte titel={`Bankverbindungen (${sicht.length})`}
               rechts={<input className="suche" type="search" placeholder="Suchen: Name, IBAN, Partner, Gemeinde …" value={suche}
                              onChange={(e) => setSuche(e.target.value)} style={{ minWidth: 260 }} />}>
          {d && !sicht.length && <Leer text="Keine Bankverbindung in dieser Auswahl." />}
          {sicht.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="liste">
                <thead><tr><th>Kontoinhaber</th><th>IBAN</th><th>Art</th><th>Zugeordnet</th><th>Stand</th><th>Zahlungen</th><th></th></tr></thead>
                <tbody>
                  {sicht.map((e) => (
                    <tr key={e.id}>
                      <td><b>{e.name}</b>{e.notiz && <div className="klein leise">{e.notiz}</div>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{e.iban_lesbar}{e.bic && <div className="klein leise">{e.bic}</div>}</td>
                      <td>{ART[e.art] ?? e.art}</td>
                      <td>{e.firma ?? e.gemeinde ?? <span className="leise">nicht zugeordnet</span>}</td>
                      <td>{e.bestaetigt_am
                        ? <span className="merker gruen">bestätigt {datum(e.bestaetigt_am)}{e.bestaetigt_von_name ? `, ${e.bestaetigt_von_name}` : ''}</span>
                        : <span className="merker warnung">Vorschlag</span>}</td>
                      <td className="klein">{e.anzahl_zahlungen ? `${e.anzahl_zahlungen}×, zuletzt ${datum(e.zuletzt_bezahlt_am)}` : 'noch keine'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {!e.bestaetigt_am && (
                          <button className="knopf klein haupt" disabled={laeuft}
                                  onClick={async () => { if (await fragen(`Bestätigen?\n\n${e.name}\n${e.iban_lesbar}\n\nNur bestätigen, wenn Name und IBAN geprüft sind.`))
                                    tu(async () => { await api.bankverbindungBestaetigen(e.id); return 'Bestätigt.'; }); }}>Bestätigen</button>
                        )}
                        <button className="knopf klein" style={{ marginLeft: 6 }} onClick={() => setBearbeiten(e)}>Aktualisieren</button>
                        <button className="knopf klein" style={{ marginLeft: 6 }} disabled={laeuft}
                                onClick={async () => { if (await fragen(`Bankverbindung entfernen?\n\n${e.name}\n${e.iban_lesbar}`))
                                  tu(async () => { await api.bankverbindungEntfernen(e.id); return 'Entfernt.'; }); }}>Entfernen</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Karte>
      </div>
      {bearbeiten && d && (
        <Bearbeiten e={bearbeiten} gemeinden={d.gemeinden} partner={d.partner} zu={() => setBearbeiten(null)}
                    fertig={(t) => { setBearbeiten(null); setHinweis(t); laden(); }} />
      )}
    </>
  );
}

function Bearbeiten({ e, gemeinden, partner, zu, fertig }: { e: any; gemeinden: any[]; partner: any[]; zu: () => void; fertig: (t: string) => void }) {
  const neu = !!e.neu;
  const [name, setName] = useState<string>(e.name ?? '');
  const [iban, setIban] = useState<string>(e.iban_lesbar ?? '');
  const [bic, setBic] = useState<string>(e.bic ?? '');
  const [art, setArt] = useState<string>(e.art ?? 'partner');
  const [sub, setSub] = useState<string>(e.subunternehmer_id ?? '');
  const [gem, setGem] = useState<string>(e.gemeinde_id ?? '');
  const [notiz, setNotiz] = useState<string>(e.notiz ?? '');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const stimmt = ibanOk(iban);
  const ibanNeu = !neu && iban.replace(/\s/g, '').toUpperCase() !== String(e.iban_lesbar ?? '').replace(/\s/g, '').toUpperCase();
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      const w = { name, bic: bic || null, art, subunternehmer_id: art === 'partner' ? (sub || null) : null,
                  gemeinde_id: art === 'behoerde' ? (gem || null) : null, notiz };
      if (neu) { await api.bankverbindungNeu({ ...w, iban }); fertig('Angelegt und bestätigt.'); }
      else {
        await api.bankverbindungAendern(e.id, ibanNeu ? { ...w, iban } : w);
        fertig(ibanNeu ? 'Aktualisiert. Die neue IBAN gilt ab sofort und ist bestätigt.'
          : name !== e.name ? 'Gespeichert. Der Name ist neu, deshalb ist die Bankverbindung wieder zu bestätigen.' : 'Gespeichert.');
      }
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={neu ? 'Neue Bankverbindung' : 'Bankverbindung aktualisieren'} untertitel={neu ? 'Wird beim Anlegen bestätigt' : e.iban_lesbar} breite={680} zu={zu}>
      <form onSubmit={(ev) => { ev.preventDefault(); if (stimmt && name.trim().length >= 3) speichern(); }}>
        <Fehlerkasten lage={fehler} />
        <div className="reihe" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Text name="name" titel="Kontoinhaber" wert={name} setze={setName} hoechstens={140} breite={400} fehler={fehler} />
          <div className="feld" style={{ width: 200 }}>
            <label htmlFor="art">Art</label>
            <select id="art" value={art} onChange={(x) => setArt(x.target.value)}>
              <option value="partner">Partner</option><option value="behoerde">Kasse (Behörde)</option><option value="sonstig">Sonstige</option>
            </select>
          </div>
          <Text name="iban" titel="IBAN" wert={iban} setze={setIban} hoechstens={40} breite={400} fehler={fehler}
                hinweis={iban.trim() ? (ibanOk(iban) ? (ibanNeu ? 'Prüfziffer stimmt. Neue IBAN: gilt nach dem Speichern als bestätigt.' : 'Prüfziffer stimmt.') : 'Die Prüfziffer stimmt nicht.') : undefined} />
          <Text name="bic" titel="BIC (freiwillig)" wert={bic} setze={setBic} hoechstens={20} breite={200} fehler={fehler} />
          {art === 'partner' && (
            <div className="feld" style={{ width: 400 }}>
              <label htmlFor="sub">Partner</label>
              <select id="sub" value={sub} onChange={(x) => setSub(x.target.value)}>
                <option value="">nicht zugeordnet</option>
                {partner.map((p) => <option key={p.id} value={p.id}>{p.firma}</option>)}
              </select>
              <div className="feldhinweis">Hierhin gehen die Rechnungen dieses Partners.</div>
            </div>
          )}
          {art === 'behoerde' && (
            <div className="feld" style={{ width: 400 }}>
              <label htmlFor="gem">Gemeinde</label>
              <select id="gem" value={gem} onChange={(x) => setGem(x.target.value)}>
                <option value="">nicht zugeordnet</option>
                {gemeinden.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          )}
          <Text name="notiz" titel="Notiz" mehrzeilig wert={notiz} setze={setNotiz} hoechstens={1000} breite={620} fehler={fehler} />
        </div>
        <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
          <button className="knopf haupt" disabled={laeuft || !stimmt || name.trim().length < 3}>{neu ? 'Anlegen und bestätigen' : 'Speichern'}</button>
          <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}
